import { Conversation, ConversationAiSettings, ConversationAiToolOption, ConversationCustomList, ConversationInteractiveButtonItem, ConversationInteractiveButtons, ConversationInteractiveList, ConversationInteractiveListRow, ConversationInteractiveListSection, ConversationKind, ConversationMention, ConversationMentionInput, ConversationMessagesPage, ConversationNote, ConversationNoteVisibility, Message, ConversationParticipantRef, ConversationParticipantOption, QuotedMessage, ScheduledInboxItem, ScheduledMessage, UpdateConversationAiSettingsRequest } from '../types/Conversation';
import { apiRequest } from '../../../shared/services/apiClient';
import { FilterAiMuted, FilterChannel, FilterStatus } from '../types/conversationNav';
import { CreateTaskRequest, TaskItem } from '../../tasks/types/Task';
import { normalizeTask } from '../../tasks/services/taskService';

export const DEFAULT_CONVERSATION_MESSAGE_PAGE_SIZE = 50;
export const DEFAULT_CONVERSATION_LIST_PAGE_SIZE = 25;

export interface ConversationListPage {
    items: Conversation[];
    page: number;
    pageSize: number;
    total: number;
    hasNext: boolean;
}

const PARTICIPANT_TYPE_BY_NUMBER: Record<number, ConversationParticipantRef['type']> = {
    0: 'companyMember',
    1: 'customer',
    2: 'aiAssistant',
    3: 'lead',
    4: 'system',
    5: 'businessSuite',
};

const normalizeParticipantType = (value: any): ConversationParticipantRef['type'] => {
    if (typeof value === 'number' && Number.isInteger(value) && value in PARTICIPANT_TYPE_BY_NUMBER) {
        return PARTICIPANT_TYPE_BY_NUMBER[value];
    }

    const normalized = String(value ?? '').trim().toLowerCase();
    if (/^\d+$/.test(normalized)) {
        const parsed = Number(normalized);
        if (parsed in PARTICIPANT_TYPE_BY_NUMBER) {
            return PARTICIPANT_TYPE_BY_NUMBER[parsed];
        }
    }

    switch (normalized) {
        case 'companymember':
        case 'company_member':
        case 'company-member':
            return 'companyMember';
        case 'aiassistant':
        case 'ai_assistant':
        case 'ai-assistant':
            return 'aiAssistant';
        case 'lead':
            return 'lead';
        case 'system':
            return 'system';
        case 'businesssuite':
        case 'business_suite':
        case 'business-suite':
            return 'businessSuite';
        case 'customer':
        default:
            return 'customer';
    }
};

const normalizeContactChannelType = (value: any): ConversationParticipantOption['contactChannelType'] | undefined => {
    const normalized = String(value ?? '').trim().toLowerCase();
    switch (normalized) {
        case 'phone':
            return 'phone';
        case 'whatsapp':
        case 'whats_app':
        case 'whats-app':
        case 'whatsappcloud':
            return 'whatsApp';
        case 'instagram':
            return 'instagram';
        case 'telegram':
            return 'telegram';
        case 'messenger':
            return 'messenger';
        case 'tiktok':
        case 'tik_tok':
            return 'tikTok';
        case 'signal':
            return 'signal';
        case 'other':
            return 'other';
        default:
            return undefined;
    }
};

export interface AiDraftSuggestion {
    conversationId: string;
    content: string;
    generatedAt: string;
}

export interface AiConversationSummary {
    conversationId: string;
    content: string;
    generatedAt: string;
}

export interface ConversationMemoryState {
    conversationId: string;
    content?: string;
    isEmpty: boolean;
}

export interface MessageTemplate {
    id: string;
    name: string;
    content: string;
    scope?: 'conversation' | 'reminder';
    createdAt: string;
    updatedAt: string;
}

export interface ConversationNotesState {
    conversationId: string;
    note?: string;
    visibleNoteCount: number;
    notes: ConversationNote[];
}

export interface ConversationAiOperationItem {
    id: string;
    conversationId?: string;
    employeeId?: string;
    runId?: string;
    modelName?: string;
    pluginName: string;
    functionName: string;
    accessMode: string;
    status: string;
    triggeredBy: string;
    inputTokens?: number;
    outputTokens?: number;
    totalTokens?: number;
    operationDetails?: string;
    inputJson?: string;
    outputJson?: string;
    error?: string;
    createdAt: string;
}

export interface ConversationAiRunUsageSummary {
    runId: string;
    operationCount: number;
    completionCount: number;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    startedAt: string;
    endedAt: string;
}

export interface ConversationAiOperationsPage {
    conversationId: string;
    items: ConversationAiOperationItem[];
    runSummaries: ConversationAiRunUsageSummary[];
    hasMore: boolean;
    nextBefore?: string;
}

const normalizeUtcTimestamp = (value: any, fallbackIso?: string): string => {
    const fallback = fallbackIso || new Date().toISOString();
    if (value == null) return fallback;

    const raw = String(value).trim();
    if (!raw) return fallback;

    // If backend omits timezone, interpret it as UTC to avoid mixed UTC/local rendering.
    const hasTimezone = /(?:Z|[+-]\d{2}:\d{2})$/i.test(raw);
    const utcCandidate = hasTimezone ? raw : `${raw}Z`;
    const parsed = new Date(utcCandidate);
    if (Number.isNaN(parsed.getTime())) return fallback;

    return parsed.toISOString();
};

const normalizeOptionalNumber = (value: any): number | undefined => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string' && value.trim()) {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }

    return undefined;
};

/** `null` = unlimited; `undefined` = field absent (caller may fall back). */
const normalizeInteractiveButtonMaxCount = (value: any): number | null | undefined => {
    if (value === undefined) {
        return undefined;
    }
    if (value === null) {
        return null;
    }
    return normalizeOptionalNumber(value);
};

const normalizeOptionalString = (value: any): string | undefined => {
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
};

const normalizeConversationKind = (value: any): ConversationKind | undefined => {
    const normalized = String(value ?? '').trim().toLowerCase();
    switch (normalized) {
        case 'internal-direct':
        case 'internal-channel':
        case 'external-direct':
        case 'external-channel':
        case 'whatsapp-group':
        case 'unknown':
            return normalized;
        default:
            return undefined;
    }
};

const normalizeQuotedMessageId = (value: any): string | undefined => {
    if (typeof value === 'string' && value.trim()) {
        return value.trim();
    }

    if (typeof value === 'number') {
        return String(value);
    }

    return undefined;
};

const normalizeInteractiveRow = (value: any): ConversationInteractiveListRow => ({
    id: String(value?.id ?? '').trim(),
    title: String(value?.title ?? '').trim(),
    description: normalizeOptionalString(value?.description),
});

const normalizeInteractiveSection = (value: any): ConversationInteractiveListSection => ({
    title: normalizeOptionalString(value?.title),
    rows: Array.isArray(value?.rows)
        ? value.rows.map(normalizeInteractiveRow).filter((row: ConversationInteractiveListRow) => row.id && row.title)
        : [],
});

const normalizeInteractiveList = (value: any): ConversationInteractiveList | undefined => {
    if (!value || typeof value !== 'object') {
        return undefined;
    }

    const bodyText = normalizeOptionalString(value?.bodyText);
    const buttonText = normalizeOptionalString(value?.buttonText);
    const sections = Array.isArray(value?.sections)
        ? value.sections.map(normalizeInteractiveSection).filter((section: ConversationInteractiveListSection) => section.rows.length > 0)
        : [];

    if (!bodyText || !buttonText || sections.length === 0) {
        return undefined;
    }

    return {
        headerText: normalizeOptionalString(value?.headerText),
        bodyText,
        footerText: normalizeOptionalString(value?.footerText),
        buttonText,
        sections,
    };
};

const normalizeInteractiveButtonItem = (value: any): ConversationInteractiveButtonItem => ({
    id: String(value?.id ?? '').trim(),
    title: String(value?.title ?? '').trim(),
});

const normalizeInteractiveButtons = (value: any): ConversationInteractiveButtons | undefined => {
    if (!value || typeof value !== 'object') {
        return undefined;
    }

    const bodyText = normalizeOptionalString(value?.bodyText);
    const buttons = Array.isArray(value?.buttons)
        ? value.buttons.map(normalizeInteractiveButtonItem).filter((button: ConversationInteractiveButtonItem) => button.id && button.title)
        : [];

    if (!bodyText || buttons.length === 0) {
        return undefined;
    }

    return {
        headerText: normalizeOptionalString(value?.headerText),
        bodyText,
        footerText: normalizeOptionalString(value?.footerText),
        buttons,
    };
};

export interface AddConversationMessageOptions {
    messageType?: string;
    interactiveList?: ConversationInteractiveList;
    interactiveButtons?: ConversationInteractiveButtons;
    mentions?: ConversationMentionInput[];
    quotedMessageId?: string;
    previewUrl?: boolean;
    instagramReplyMode?: 'private_comment' | 'public_comment' | 'dm';
    messengerReplyMode?: 'private_comment' | 'public_comment' | 'dm';
}

export interface MarkMessagesReadOptions {
    showWhatsAppTypingIndicator?: boolean;
    showInstagramTypingIndicator?: boolean;
    showMessengerTypingIndicator?: boolean;
}

export const normalizeParticipant = (value: any): ConversationParticipantRef => ({
    type: normalizeParticipantType(value?.type),
    id: String(value?.id ?? ''),
    name: typeof value?.name === 'string' ? value.name.trim() || undefined : undefined,
    contactId: typeof value?.contactId === 'string' && value.contactId.trim() ? value.contactId.trim() : undefined,
    contactChannelId: typeof value?.contactChannelId === 'string' && value.contactChannelId.trim() ? value.contactChannelId.trim() : undefined,
    contactChannelType: ['phone', 'whatsApp', 'instagram', 'telegram', 'messenger', 'tikTok', 'signal', 'other'].includes(String(value?.contactChannelType ?? ''))
        ? String(value.contactChannelType) as ConversationParticipantRef['contactChannelType']
        : undefined,
    contactChannelLabel: typeof value?.contactChannelLabel === 'string' && value.contactChannelLabel.trim() ? value.contactChannelLabel.trim() : undefined,
    contactChannelValue: typeof value?.contactChannelValue === 'string' && value.contactChannelValue.trim() ? value.contactChannelValue.trim() : undefined,
});

const normalizeQuotedMessage = (value: any): QuotedMessage | undefined => {
    if (!value || typeof value !== 'object') {
        return undefined;
    }

    const id = String(value?.id ?? '').trim();
    if (!id) {
        return undefined;
    }

    return {
        id,
        sender: normalizeParticipant(value?.sender),
        content: String(value?.content ?? ''),
        messageType: typeof value?.messageType === 'string' && value.messageType.trim() ? value.messageType.trim().toLowerCase() : 'text',
        fileName: typeof value?.fileName === 'string' && value.fileName.trim() ? value.fileName.trim() : undefined,
        mediaUrl: typeof value?.mediaUrl === 'string' && value.mediaUrl.trim() ? value.mediaUrl.trim() : undefined,
    };
};

const normalizeMention = (value: any): ConversationMention => ({
    mentionedParticipantId: String(value?.mentionedParticipantId ?? ''),
    mentionedParticipantType: normalizeParticipantType(value?.mentionedParticipantType),
    mentionedParticipantName: String(value?.mentionedParticipantName ?? '').trim(),
    mentionText: String(value?.mentionText ?? '').trim(),
    position: Number.isFinite(Number(value?.position)) ? Number(value.position) : 0,
    length: Number.isFinite(Number(value?.length)) ? Number(value.length) : 0,
});

export const normalizeParticipantOption = (value: any): ConversationParticipantOption => ({
    type: normalizeParticipantType(value?.type),
    id: String(value?.id ?? ''),
    name: String(value?.name ?? '').trim(),
    firstName: typeof value?.firstName === 'string' && value.firstName.trim() ? value.firstName.trim() : undefined,
    lastName: typeof value?.lastName === 'string' && value.lastName.trim() ? value.lastName.trim() : undefined,
    avatarColor: typeof value?.avatarColor === 'string' ? value.avatarColor : undefined,
    avatarUrl: typeof value?.avatarUrl === 'string' && value.avatarUrl.trim()
        ? value.avatarUrl.trim()
        : typeof value?.profileImageUrl === 'string' && value.profileImageUrl.trim()
            ? value.profileImageUrl.trim()
            : typeof value?.photoUrl === 'string' && value.photoUrl.trim()
                ? value.photoUrl.trim()
                : undefined,
    email: typeof value?.email === 'string' ? value.email : undefined,
    selectionKey: `${normalizeParticipantType(value?.type)}:${String(value?.id ?? '')}`,
    contactChannelId: typeof value?.contactChannelId === 'string' && value.contactChannelId.trim() ? value.contactChannelId.trim() : undefined,
    contactChannelType: normalizeContactChannelType(value?.contactChannelType ?? value?.preferredChannelType),
    contactChannelLabel: typeof value?.contactChannelLabel === 'string' && value.contactChannelLabel.trim()
        ? value.contactChannelLabel.trim()
        : typeof value?.preferredChannelLabel === 'string' && value.preferredChannelLabel.trim()
            ? value.preferredChannelLabel.trim()
        : undefined,
    contactChannelValue: typeof value?.contactChannelValue === 'string' && value.contactChannelValue.trim()
        ? value.contactChannelValue.trim()
        : typeof value?.preferredChannelValue === 'string' && value.preferredChannelValue.trim()
            ? value.preferredChannelValue.trim()
        : undefined,
    isContactChannelPrimary: Boolean(value?.isContactChannelPrimary),
});

const normalizeChannelContext = (value: any): Message['channelContext'] => {
    const surface = String(value?.surface ?? '').trim().toLowerCase();
    if (surface !== 'comment' && surface !== 'live_comment' && surface !== 'dm') {
        return undefined;
    }

    return {
        surface: surface as NonNullable<Message['channelContext']>['surface'],
        commentId: typeof value?.commentId === 'string' && value.commentId.trim() ? value.commentId.trim() : undefined,
        mediaId: typeof value?.mediaId === 'string' && value.mediaId.trim() ? value.mediaId.trim() : undefined,
        mediaProductType: typeof value?.mediaProductType === 'string' && value.mediaProductType.trim()
            ? value.mediaProductType.trim()
            : undefined,
        instagramUsername: typeof value?.instagramUsername === 'string' && value.instagramUsername.trim()
            ? value.instagramUsername.trim()
            : undefined,
        webhookField: typeof value?.webhookField === 'string' && value.webhookField.trim()
            ? value.webhookField.trim()
            : undefined,
    };
};

const normalizeContentPreview = (value: any): Message['contentPreview'] => {
    if (!value || typeof value !== 'object') {
        return undefined;
    }

    const externalContentId = typeof value.externalContentId === 'string' ? value.externalContentId.trim() : '';
    const channel = typeof value.channel === 'string' ? value.channel.trim() : '';
    if (!externalContentId || !channel) {
        return undefined;
    }

    return {
        externalContentId,
        channel,
        contentType: typeof value.contentType === 'string' && value.contentType.trim() ? value.contentType.trim() : undefined,
        caption: typeof value.caption === 'string' && value.caption.trim() ? value.caption.trim() : undefined,
        permalink: typeof value.permalink === 'string' && value.permalink.trim() ? value.permalink.trim() : undefined,
        thumbnailUrl: typeof value.thumbnailUrl === 'string' && value.thumbnailUrl.trim() ? value.thumbnailUrl.trim() : undefined,
        mediaProductType: typeof value.mediaProductType === 'string' && value.mediaProductType.trim()
            ? value.mediaProductType.trim()
            : undefined,
        ownership: typeof value.ownership === 'string' && value.ownership.trim()
            ? value.ownership.trim()
            : undefined,
        ownerUsername: typeof value.ownerUsername === 'string' && value.ownerUsername.trim()
            ? value.ownerUsername.trim()
            : undefined,
        shareKind: typeof value.shareKind === 'string' && value.shareKind.trim()
            ? value.shareKind.trim()
            : undefined,
    };
};

export const normalizeMessage = (value: any): Message => ({
    id: String(value?.id ?? ''),
    conversationId: String(value?.conversationId ?? ''),
    sender: normalizeParticipant(value?.sender),
    content: String(value?.content ?? ''),
    timestamp: normalizeUtcTimestamp(value?.timestamp),
    status: value?.status === 'delivered' || value?.status === 'failed' ? value.status : 'sent',
    source: typeof value?.source === 'string' && value.source.trim() ? value.source.trim().toLowerCase() : 'internal',
    messageType: typeof value?.messageType === 'string' && value.messageType.trim() ? value.messageType.trim().toLowerCase() : 'text',
    mediaUrl: typeof value?.mediaUrl === 'string' && value.mediaUrl.trim() ? value.mediaUrl.trim() : undefined,
    mimeType: typeof value?.mimeType === 'string' && value.mimeType.trim() ? value.mimeType.trim() : undefined,
    fileName: typeof value?.fileName === 'string' && value.fileName.trim() ? value.fileName.trim() : undefined,
    transcription: typeof value?.transcription === 'string' && value.transcription.trim() ? value.transcription.trim() : undefined,
    transcriptionUpdatedAt: value?.transcriptionUpdatedAt ? normalizeUtcTimestamp(value.transcriptionUpdatedAt) : undefined,
    locationLatitude: normalizeOptionalNumber(value?.locationLatitude),
    locationLongitude: normalizeOptionalNumber(value?.locationLongitude),
    locationName: typeof value?.locationName === 'string' && value.locationName.trim() ? value.locationName.trim() : undefined,
    locationAddress: typeof value?.locationAddress === 'string' && value.locationAddress.trim() ? value.locationAddress.trim() : undefined,
    locationUrl: typeof value?.locationUrl === 'string' && value.locationUrl.trim() ? value.locationUrl.trim() : undefined,
    quotedMessageId: normalizeQuotedMessageId(value?.quotedMessageId),
    quotedMessage: normalizeQuotedMessage(value?.quotedMessage),
    interactiveList: normalizeInteractiveList(value?.interactiveList),
    interactiveButtons: normalizeInteractiveButtons(value?.interactiveButtons),
    mentions: Array.isArray(value?.mentions)
        ? value.mentions.map(normalizeMention).filter((mention: ConversationMention) => mention.mentionedParticipantId && mention.mentionText && mention.length > 0)
        : undefined,
    readBy: Array.isArray(value?.readBy) ? value.readBy.map(normalizeParticipant) : [],
    reactions: Object.entries(value?.reactions || {}).reduce((acc, [emoji, reactors]) => {
        acc[emoji] = Array.isArray(reactors) ? reactors.map(normalizeParticipant) : [];
        return acc;
    }, {} as { [emoji: string]: ConversationParticipantRef[] }),
    whatsAppMessageId: typeof value?.whatsAppMessageId === 'string' && value.whatsAppMessageId.trim()
        ? value.whatsAppMessageId.trim()
        : undefined,
    whatsAppStatusValue: typeof value?.whatsAppStatusValue === 'string' && value.whatsAppStatusValue.trim()
        ? value.whatsAppStatusValue.trim()
        : undefined,
    whatsAppBizOpaqueCallbackData: typeof value?.whatsAppBizOpaqueCallbackData === 'string' && value.whatsAppBizOpaqueCallbackData.trim()
        ? value.whatsAppBizOpaqueCallbackData.trim()
        : undefined,
    instagramMessageId: typeof value?.instagramMessageId === 'string' && value.instagramMessageId.trim()
        ? value.instagramMessageId.trim()
        : undefined,
    instagramStatusValue: typeof value?.instagramStatusValue === 'string' && value.instagramStatusValue.trim()
        ? value.instagramStatusValue.trim()
        : undefined,
    instagramRecipientId: typeof value?.instagramRecipientId === 'string' && value.instagramRecipientId.trim()
        ? value.instagramRecipientId.trim()
        : undefined,
    messengerMessageId: typeof value?.messengerMessageId === 'string' && value.messengerMessageId.trim()
        ? value.messengerMessageId.trim()
        : undefined,
    messengerStatusValue: typeof value?.messengerStatusValue === 'string' && value.messengerStatusValue.trim()
        ? value.messengerStatusValue.trim()
        : undefined,
    messengerRecipientId: typeof value?.messengerRecipientId === 'string' && value.messengerRecipientId.trim()
        ? value.messengerRecipientId.trim()
        : undefined,
    channelContext: normalizeChannelContext(value?.channelContext),
    instagramPrivateReplyCommentId: typeof value?.instagramPrivateReplyCommentId === 'string' && value.instagramPrivateReplyCommentId.trim()
        ? value.instagramPrivateReplyCommentId.trim()
        : undefined,
    instagramPublicReplyCommentId: typeof value?.instagramPublicReplyCommentId === 'string' && value.instagramPublicReplyCommentId.trim()
        ? value.instagramPublicReplyCommentId.trim()
        : undefined,
    instagramReplyMode: value?.instagramReplyMode === 'private_comment'
        || value?.instagramReplyMode === 'public_comment'
        || value?.instagramReplyMode === 'dm'
        ? value.instagramReplyMode
        : undefined,
    instagramCommentHidden: typeof value?.instagramCommentHidden === 'boolean' ? value.instagramCommentHidden : undefined,
    instagramCommentDeleted: typeof value?.instagramCommentDeleted === 'boolean' ? value.instagramCommentDeleted : undefined,
    messengerPrivateReplyCommentId: typeof value?.messengerPrivateReplyCommentId === 'string' && value.messengerPrivateReplyCommentId.trim()
        ? value.messengerPrivateReplyCommentId.trim()
        : undefined,
    messengerPublicReplyCommentId: typeof value?.messengerPublicReplyCommentId === 'string' && value.messengerPublicReplyCommentId.trim()
        ? value.messengerPublicReplyCommentId.trim()
        : undefined,
    messengerReplyMode: value?.messengerReplyMode === 'private_comment'
        || value?.messengerReplyMode === 'public_comment'
        || value?.messengerReplyMode === 'dm'
        ? value.messengerReplyMode
        : undefined,
    messengerCommentHidden: typeof value?.messengerCommentHidden === 'boolean' ? value.messengerCommentHidden : undefined,
    messengerCommentDeleted: typeof value?.messengerCommentDeleted === 'boolean' ? value.messengerCommentDeleted : undefined,
    contentPreview: normalizeContentPreview(value?.contentPreview),
});

export const normalizeConversationNote = (value: any): ConversationNote => ({
    id: String(value?.id ?? ''),
    text: String(value?.text ?? '').trim(),
    visibility: String(value?.visibility ?? '').trim().toLowerCase() === 'public' ? 'public' : 'private',
    createdByEmployeeId: String(value?.createdByEmployeeId ?? ''),
    createdByEmployeeName: typeof value?.createdByEmployeeName === 'string' ? value.createdByEmployeeName.trim() || undefined : undefined,
    createdAt: normalizeUtcTimestamp(value?.createdAt),
    updatedAt: normalizeUtcTimestamp(value?.updatedAt),
});

const normalizeNotes = (value: any): ConversationNote[] => (
    Array.isArray(value?.notes)
        ? value.notes.map(normalizeConversationNote).filter((note: ConversationNote) => note.id && note.text)
        : []
);

const normalizeVisibleNoteCount = (value: any): number => normalizeOptionalNumber(value?.visibleNoteCount) ?? 0;

export const normalizeConversationNotesState = (value: any): ConversationNotesState => ({
    conversationId: String(value?.conversationId ?? ''),
    note: normalizeOptionalString(value?.note),
    visibleNoteCount: normalizeVisibleNoteCount(value),
    notes: normalizeNotes(value),
});

export const normalizeConversationAiOperation = (value: any): ConversationAiOperationItem => ({
    id: String(value?.id ?? ''),
    conversationId: normalizeOptionalString(value?.conversationId),
    employeeId: normalizeOptionalString(value?.employeeId),
    runId: normalizeOptionalString(value?.runId),
    modelName: normalizeOptionalString(value?.modelName),
    pluginName: String(value?.pluginName ?? '').trim(),
    functionName: String(value?.functionName ?? '').trim(),
    accessMode: String(value?.accessMode ?? '').trim(),
    status: String(value?.status ?? '').trim(),
    triggeredBy: String(value?.triggeredBy ?? '').trim(),
    inputTokens: normalizeOptionalNumber(value?.inputTokens),
    outputTokens: normalizeOptionalNumber(value?.outputTokens),
    totalTokens: normalizeOptionalNumber(value?.totalTokens),
    operationDetails: normalizeOptionalString(value?.operationDetails),
    inputJson: normalizeOptionalString(value?.inputJson),
    outputJson: normalizeOptionalString(value?.outputJson),
    error: normalizeOptionalString(value?.error),
    createdAt: normalizeUtcTimestamp(value?.createdAt),
});

export const normalizeConversationAiRunUsageSummary = (value: any): ConversationAiRunUsageSummary => ({
    runId: String(value?.runId ?? ''),
    operationCount: normalizeOptionalNumber(value?.operationCount) ?? 0,
    completionCount: normalizeOptionalNumber(value?.completionCount) ?? 0,
    inputTokens: normalizeOptionalNumber(value?.inputTokens) ?? 0,
    outputTokens: normalizeOptionalNumber(value?.outputTokens) ?? 0,
    totalTokens: normalizeOptionalNumber(value?.totalTokens) ?? 0,
    startedAt: normalizeUtcTimestamp(value?.startedAt),
    endedAt: normalizeUtcTimestamp(value?.endedAt),
});

export const normalizeConversationAiOperationsPage = (value: any): ConversationAiOperationsPage => ({
    conversationId: String(value?.conversationId ?? ''),
    items: Array.isArray(value?.items)
        ? value.items.map(normalizeConversationAiOperation).filter((item: ConversationAiOperationItem) => item.id && item.pluginName && item.functionName)
        : [],
    runSummaries: Array.isArray(value?.runSummaries)
        ? value.runSummaries
            .map(normalizeConversationAiRunUsageSummary)
            .filter((item: ConversationAiRunUsageSummary) => Boolean(item.runId))
        : [],
    hasMore: Boolean(value?.hasMore),
    nextBefore: value?.nextBefore ? normalizeUtcTimestamp(value.nextBefore) : undefined,
});

export const normalizeConversation = (value: any): Conversation => {
    const messages = Array.isArray(value?.messages) ? value.messages.map(normalizeMessage) : [];
    const totalMessageCount = normalizeOptionalNumber(value?.totalMessageCount) ?? messages.length;

    return {
        id: String(value?.id ?? ''),
        title: value?.title ? String(value.title) : undefined,
        participants: Array.isArray(value?.participants) ? value.participants.map(normalizeParticipant) : [],
        messages,
        totalMessageCount,
        hasMoreMessages: Boolean(value?.hasMoreMessages) || totalMessageCount > messages.length,
        createdAt: normalizeUtcTimestamp(value?.createdAt),
        updatedAt: normalizeUtcTimestamp(value?.updatedAt),
        isSigned: Boolean(value?.isSigned),
        isAiAssistantMuted: Boolean(value?.isAiAssistantMuted),
        isFavorite: Boolean(value?.isFavorite),
        isPinned: Boolean(value?.isPinned),
        isSpam: Boolean(value?.isSpam ?? value?.isHidden),
        isHidden: Boolean(value?.isSpam ?? value?.isHidden),
        note: normalizeOptionalString(value?.note),
        aiHistoryStartAtUtc: value?.aiHistoryStartAtUtc ? normalizeUtcTimestamp(value?.aiHistoryStartAtUtc) : undefined,
        notes: normalizeNotes(value),
        visibleNoteCount: normalizeVisibleNoteCount(value),
        assignedToEmployeeId: value?.assignedToEmployeeId ? String(value.assignedToEmployeeId) : undefined,
        assignedToEmployeeName: typeof value?.assignedToEmployeeName === 'string' ? value.assignedToEmployeeName.trim() || undefined : undefined,
        hasExternalParticipant: Boolean(value?.hasExternalParticipant),
        conversationKind: normalizeConversationKind(value?.conversationKind),
        containsWhatsAppMessages: Boolean(value?.containsWhatsAppMessages),
        containsInstagramMessages: Boolean(value?.containsInstagramMessages),
        containsInstagramComments: Boolean(value?.containsInstagramComments),
        containsMessengerComments: Boolean(value?.containsMessengerComments),
        interactiveButtonMaxCount: normalizeInteractiveButtonMaxCount(value?.interactiveButtonMaxCount),
        channelAccountId: value?.channelAccountId ? String(value.channelAccountId) : undefined,
        channelAccountDisplayName: typeof value?.channelAccountDisplayName === 'string'
            ? value.channelAccountDisplayName.trim() || undefined
            : undefined,
        whatsAppConnectionKind: value?.whatsAppConnectionKind === 'app'
            ? 'app'
            : value?.whatsAppConnectionKind === 'cloud'
                ? 'cloud'
                : undefined,
        whatsAppGroupJid: normalizeOptionalString(value?.whatsAppGroupJid),
        whatsAppGroupPictureUrl: normalizeOptionalString(value?.whatsAppGroupPictureUrl),
        customListId: value?.customListId ? String(value.customListId) : undefined,
        customListName: typeof value?.customListName === 'string'
            ? value.customListName.trim() || undefined
            : undefined,
    };
};

export const normalizeConversationCustomList = (value: any): ConversationCustomList => ({
    id: String(value?.id ?? ''),
    name: String(value?.name ?? '').trim(),
    sortOrder: Number(value?.sortOrder ?? 0),
    conversationCount: Number(value?.conversationCount ?? 0),
});

export const normalizeConversationMessagesPage = (value: any): ConversationMessagesPage => ({
    conversationId: String(value?.conversationId ?? ''),
    messages: Array.isArray(value?.messages) ? value.messages.map(normalizeMessage) : [],
    loadedCount: normalizeOptionalNumber(value?.loadedCount) ?? 0,
    totalCount: normalizeOptionalNumber(value?.totalCount) ?? 0,
    hasMoreMessages: Boolean(value?.hasMoreMessages ?? value?.hasMore),
});

export const normalizeAiDraftSuggestion = (value: any): AiDraftSuggestion => ({
    conversationId: String(value?.conversationId ?? ''),
    content: String(value?.content ?? '').trim(),
    generatedAt: normalizeUtcTimestamp(value?.generatedAt),
});

export const normalizeAiConversationSummary = (value: any): AiConversationSummary => ({
    conversationId: String(value?.conversationId ?? ''),
    content: String(value?.content ?? '').trim(),
    generatedAt: normalizeUtcTimestamp(value?.generatedAt),
});

export const normalizeConversationMemoryState = (value: any): ConversationMemoryState => ({
    conversationId: String(value?.conversationId ?? ''),
    content: normalizeOptionalString(value?.content),
    isEmpty: Boolean(value?.isEmpty ?? !normalizeOptionalString(value?.content)),
});

export const normalizeMessageTemplate = (value: any): MessageTemplate => ({
    id: String(value?.id ?? ''),
    name: String(value?.name ?? '').trim(),
    content: String(value?.content ?? ''),
    scope: value?.scope === 'reminder' ? 'reminder' : 'conversation',
    createdAt: normalizeUtcTimestamp(value?.createdAt),
    updatedAt: normalizeUtcTimestamp(value?.updatedAt),
});

export const normalizeScheduledMessage = (value: any): ScheduledMessage => ({
    id: String(value?.id ?? ''),
    conversationId: String(value?.conversationId ?? ''),
    sender: normalizeParticipant(value?.sender),
    content: String(value?.content ?? ''),
    quotedMessageId: normalizeQuotedMessageId(value?.quotedMessageId),
    quotedMessage: normalizeQuotedMessage(value?.quotedMessage),
    messageType: typeof value?.messageType === 'string' && value.messageType.trim() ? value.messageType.trim().toLowerCase() : 'text',
    mediaUrl: typeof value?.mediaUrl === 'string' && value.mediaUrl.trim() ? value.mediaUrl.trim() : undefined,
    mimeType: typeof value?.mimeType === 'string' && value.mimeType.trim() ? value.mimeType.trim() : undefined,
    fileName: typeof value?.fileName === 'string' && value.fileName.trim() ? value.fileName.trim() : undefined,
    scheduledAtUtc: normalizeUtcTimestamp(value?.scheduledAtUtc),
    status: typeof value?.status === 'string' ? value.status : 'pending',
    dispatchedAtUtc: value?.dispatchedAtUtc ? normalizeUtcTimestamp(value?.dispatchedAtUtc) : undefined,
    createdAt: normalizeUtcTimestamp(value?.createdAt),
});

const appendSenderToFormData = (formData: FormData, sender: ConversationParticipantRef) => {
    formData.append('senderType', sender.type);
    formData.append('senderId', sender.id);
};

interface UpdateConversationPreferencesInput {
    isFavorite?: boolean;
    isPinned?: boolean;
    isSpam?: boolean;
    /** @deprecated Use isSpam */
    isHidden?: boolean;
    note?: string | null;
    aiHistoryStartAtUtc?: string | null;
}

export interface GetConversationsOptions {
    spamOnly?: boolean;
    /** @deprecated Use spamOnly */
    hiddenOnly?: boolean;
    customListId?: string;
    conversationKind?: 'direct' | 'channel' | 'external' | 'whatsapp-direct' | ConversationKind;
    status?: FilterStatus;
    aiMuted?: FilterAiMuted;
    channel?: FilterChannel;
    date?: string;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    page?: number;
    pageSize?: number;
    privateMessagesOnly?: boolean;
    contactId?: string;
}

interface GetConversationByIdOptions {
    includeSpam?: boolean;
    /** @deprecated Use includeSpam */
    includeHidden?: boolean;
    messagePageSize?: number;
    includeMessages?: boolean;
    ifUnchangedSince?: string;
}

interface GetConversationMessagesPageOptions {
    loadedCount: number;
    pageSize?: number;
    includeSpam?: boolean;
    /** @deprecated Use includeSpam */
    includeHidden?: boolean;
}

interface GetConversationAiOperationsOptions {
    limit?: number;
    before?: string;
    status?: string;
    plugin?: string;
    triggeredBy?: string;
    runId?: string;
}

interface UpdateConversationAssignmentInput {
    assignedToEmployeeId: string | null;
}

interface CreateConversationNoteInput {
    text: string;
    visibility: ConversationNoteVisibility;
}

interface UpdateConversationNoteInput {
    text: string;
}

interface LinkConversationTaskInput {
    messageId?: string;
    isPrimary?: boolean;
}

const toConversationTaskRequest = (request: CreateTaskRequest): Record<string, unknown> => ({
    ...request,
    dueTime: request.dueTime?.trim() ? (/^\d{2}:\d{2}$/.test(request.dueTime.trim()) ? `${request.dueTime.trim()}:00` : request.dueTime.trim()) : undefined,
    categoryId: request.categoryId || undefined,
});

interface AddConversationParticipantsInput {
    participants: ConversationParticipantRef[];
}

const pickItems = (payload: any): any[] => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.items)) return payload.data.items;
    return [];
};

const appendIncludeSpamQuery = (query: URLSearchParams, options?: { includeSpam?: boolean; includeHidden?: boolean }) => {
    if (options?.includeSpam || options?.includeHidden) {
        query.set('includeSpam', 'true');
    }
};

const buildConversationsQuery = (options?: GetConversationsOptions): URLSearchParams => {
    const query = new URLSearchParams({ IncludeMessages: 'true' });
    if (options?.spamOnly || options?.hiddenOnly) {
        query.set('SpamOnly', 'true');
    }

    if (options?.privateMessagesOnly) {
        query.set('PrivateMessagesOnly', 'true');
    }

    if (options?.customListId?.trim()) {
        query.set('CustomListId', options.customListId.trim());
    }

    if (options?.contactId?.trim()) {
        query.set('ContactId', options.contactId.trim());
    }

    if (options?.conversationKind && options.conversationKind !== 'unknown') {
        query.set('ConversationKind', options.conversationKind);
    }

    if (options?.status && options.status !== 'all') {
        query.set('Status', options.status);
    }

    if (options?.aiMuted && options.aiMuted !== 'all') {
        query.set('AiMuted', options.aiMuted);
    }

    if (options?.channel && options.channel !== 'all') {
        query.set('Channel', options.channel === 'whatsApp' ? 'whatsapp' : options.channel);
    }

    if (options?.date) {
        const start = new Date(`${options.date}T00:00:00`);
        const end = new Date(`${options.date}T23:59:59.999`);

        if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
            query.set('DateFrom', start.toISOString());
            query.set('DateTo', end.toISOString());
        }
    }

    if (options?.sortBy?.trim()) {
        query.set('SortBy', options.sortBy.trim());
    }

    if (options?.sortDir) {
        query.set('SortDir', options.sortDir);
    }

    const page = options?.page && options.page > 0 ? options.page : 1;
    const pageSize = options?.pageSize && options.pageSize > 0
        ? options.pageSize
        : DEFAULT_CONVERSATION_LIST_PAGE_SIZE;
    query.set('Page', String(page));
    query.set('PageSize', String(pageSize));

    return query;
};

const normalizeConversationListPage = (payload: any): ConversationListPage => {
    const items = pickItems(payload).map(normalizeConversation);
    return {
        items,
        page: Number(payload?.page ?? payload?.Page ?? 1),
        pageSize: Number(payload?.pageSize ?? payload?.PageSize ?? items.length),
        total: Number(payload?.total ?? payload?.Total ?? items.length),
        hasNext: Boolean(payload?.hasNext ?? payload?.HasNext),
    };
};

const pickSingle = (payload: any): any => {
    if (!payload || typeof payload !== 'object') return null;
    if (payload.data && typeof payload.data === 'object') return payload.data;
    return payload;
};

const normalizeConversationAiToolOption = (item: any): ConversationAiToolOption => ({
    functionId: String(item?.functionId ?? item?.FunctionId ?? ''),
    displayName: String(item?.displayName ?? item?.DisplayName ?? item?.functionId ?? item?.FunctionId ?? ''),
    pluginName: String(item?.pluginName ?? item?.PluginName ?? ''),
    enabled: Boolean(item?.enabled ?? item?.Enabled),
});

const normalizeConversationAiSettings = (item: any): ConversationAiSettings => {
    const tools = Array.isArray(item?.availableTools)
        ? item.availableTools
        : Array.isArray(item?.AvailableTools)
            ? item.AvailableTools
            : [];
    const enabled = Array.isArray(item?.enabledFunctionIds)
        ? item.enabledFunctionIds
        : Array.isArray(item?.EnabledFunctionIds)
            ? item.EnabledFunctionIds
            : [];
    return {
        conversationId: String(item?.conversationId ?? item?.ConversationId ?? ''),
        useCaseId: String(item?.useCaseId ?? item?.UseCaseId ?? ''),
        instructionOverride: item?.instructionOverride ?? item?.InstructionOverride ?? null,
        inheritTools: Boolean(item?.inheritTools ?? item?.InheritTools ?? true),
        enabledFunctionIds: enabled.map((id: unknown) => String(id)).filter(Boolean),
        availableTools: tools.map(normalizeConversationAiToolOption).filter((tool: ConversationAiToolOption) => tool.functionId),
    };
};

export const conversationService = {
    getMessageTemplates: async (): Promise<MessageTemplate[]> => {
        const payload = await apiRequest<any>('/api/v1/conversations/templates?scope=conversation');
        return pickItems(payload)
            .map(normalizeMessageTemplate)
            .filter(template => template.id && template.name && template.content);
    },

    createMessageTemplate: async (name: string, content: string): Promise<MessageTemplate> => {
        const payload = await apiRequest<any>('/api/v1/conversations/templates', {
            method: 'POST',
            body: JSON.stringify({ name, content, scope: 'conversation' }),
        });

        const item = pickSingle(payload);
        if (!item) {
            throw new Error('Backend returned empty message template payload.');
        }

        return normalizeMessageTemplate(item);
    },

    updateMessageTemplate: async (id: string, name: string, content: string): Promise<MessageTemplate> => {
        const payload = await apiRequest<any>(`/api/v1/conversations/templates/${encodeURIComponent(id)}`, {
            method: 'PUT',
            body: JSON.stringify({ name, content }),
        });

        const item = pickSingle(payload);
        if (!item) {
            throw new Error('Backend returned empty message template payload.');
        }

        return normalizeMessageTemplate(item);
    },

    getParticipantOptions: async (): Promise<ConversationParticipantOption[]> => {
        const payload = await apiRequest<any>('/api/v1/conversations/participants');
        return pickItems(payload).map(normalizeParticipantOption).filter(option => option.id && option.name);
    },

    getMentionSuggestions: async (search?: string): Promise<ConversationParticipantOption[]> => {
        const query = search?.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
        const payload = await apiRequest<any>(`/api/v1/conversations/mention-suggestions${query}`);
        return pickItems(payload).map(normalizeParticipantOption).filter(option => option.id && option.name);
    },

    getConversationsPage: async (options?: GetConversationsOptions): Promise<ConversationListPage> => {
        const query = buildConversationsQuery(options);
        const payload = await apiRequest<any>(`/api/v1/conversations?${query.toString()}`);
        return normalizeConversationListPage(payload);
    },

    listCustomLists: async (): Promise<ConversationCustomList[]> => {
        const payload = await apiRequest<any>('/api/v1/conversations/custom-lists');
        return pickItems(payload)
            .map(normalizeConversationCustomList)
            .filter(list => list.id && list.name);
    },

    createCustomList: async (name: string): Promise<ConversationCustomList> => {
        const payload = await apiRequest<any>('/api/v1/conversations/custom-lists', {
            method: 'POST',
            body: JSON.stringify({ name }),
        });
        const item = pickSingle(payload);
        if (!item) {
            throw new Error('Backend returned empty custom list payload.');
        }

        return normalizeConversationCustomList(item);
    },

    updateCustomList: async (listId: string, name: string): Promise<ConversationCustomList> => {
        const payload = await apiRequest<any>(`/api/v1/conversations/custom-lists/${encodeURIComponent(listId)}`, {
            method: 'PATCH',
            body: JSON.stringify({ name }),
        });
        const item = pickSingle(payload);
        if (!item) {
            throw new Error('Backend returned empty custom list payload.');
        }

        return normalizeConversationCustomList(item);
    },

    deleteCustomList: async (listId: string): Promise<void> => {
        await apiRequest<void>(`/api/v1/conversations/custom-lists/${encodeURIComponent(listId)}`, {
            method: 'DELETE',
        });
    },

    moveConversationToCustomList: async (conversationId: string, customListId: string | null): Promise<Conversation | null> => {
        const payload = await apiRequest<any>(`/api/v1/conversations/${encodeURIComponent(conversationId)}/custom-list`, {
            method: 'PUT',
            body: JSON.stringify({ customListId }),
        });
        const item = pickSingle(payload);
        return item ? normalizeConversation(item) : null;
    },

    moveConversationsToCustomList: async (conversationIds: string[], customListId: string | null): Promise<Conversation[]> => {
        const payload = await apiRequest<any>('/api/v1/conversations/custom-list/move', {
            method: 'POST',
            body: JSON.stringify({ conversationIds, customListId }),
        });
        return pickItems(payload).map(normalizeConversation).filter(conversation => conversation.id);
    },

    getConversations: async (options?: GetConversationsOptions): Promise<Conversation[]> => {
        const page = await conversationService.getConversationsPage(options);
        return page.items;
    },

    getConversationById: async (id: string, options?: GetConversationByIdOptions): Promise<Conversation | null> => {
        try {
            const query = new URLSearchParams();
            appendIncludeSpamQuery(query, options);

            if (typeof options?.messagePageSize === 'number' && options.messagePageSize > 0) {
                query.set('messagePageSize', String(options.messagePageSize));
            }

            if (options?.includeMessages === false) {
                query.set('includeMessages', 'false');
            }

            if (options?.ifUnchangedSince?.trim()) {
                query.set('ifUnchangedSince', options.ifUnchangedSince.trim());
            }

            const suffix = query.size > 0 ? `?${query.toString()}` : '';
            const payload = await apiRequest<any>(`/api/v1/conversations/${id}${suffix}`);
            const item = pickSingle(payload);
            return item ? normalizeConversation(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    getConversationMessagesPage: async (id: string, options: GetConversationMessagesPageOptions): Promise<ConversationMessagesPage | null> => {
        try {
            const query = new URLSearchParams({
                loadedCount: String(Math.max(0, options.loadedCount || 0)),
                pageSize: String(options.pageSize && options.pageSize > 0 ? options.pageSize : DEFAULT_CONVERSATION_MESSAGE_PAGE_SIZE),
            });

            appendIncludeSpamQuery(query, options);

            const payload = await apiRequest<any>(`/api/v1/conversations/${id}/messages?${query.toString()}`);
            const item = pickSingle(payload);
            return item ? normalizeConversationMessagesPage(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    findExistingExternalConversation: async (
        participant: ConversationParticipantRef,
        options?: { includeSpam?: boolean; includeHidden?: boolean; channelAccountId?: string },
    ): Promise<Conversation | null> => {
        const query = new URLSearchParams({
            participantType: participant.type,
            participantId: participant.id,
        });

        if (participant.contactChannelId?.trim()) {
            query.set('contactChannelId', participant.contactChannelId.trim());
        }

        if (options?.channelAccountId?.trim()) {
            query.set('channelAccountId', options.channelAccountId.trim());
        }

        appendIncludeSpamQuery(query, options);

        const payload = await apiRequest<any | null>(`/api/v1/conversations/existing-external?${query.toString()}`);
        const item = pickSingle(payload);
        return item ? normalizeConversation(item) : null;
    },

    findExistingDirectConversation: async (participantId: string, options?: { includeSpam?: boolean; includeHidden?: boolean }): Promise<Conversation | null> => {
        const query = new URLSearchParams({
            participantId,
        });

        appendIncludeSpamQuery(query, options);

        const payload = await apiRequest<any | null>(`/api/v1/conversations/existing-direct?${query.toString()}`);
        const item = pickSingle(payload);
        return item ? normalizeConversation(item) : null;
    },

    addMessage: async (
        conversationId: string,
        sender: ConversationParticipantRef,
        content: string,
        options?: AddConversationMessageOptions
    ): Promise<Message | null> => {
        try {
            const interactiveList = normalizeInteractiveList(options?.interactiveList);
            const interactiveButtons = normalizeInteractiveButtons(options?.interactiveButtons);
            const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/messages`, {
                method: 'POST',
                body: JSON.stringify({
                    sender,
                    content,
                    ...(options?.quotedMessageId ? { quotedMessageId: options.quotedMessageId } : {}),
                    ...(options?.messageType ? { messageType: options.messageType } : {}),
                    ...(interactiveList ? { interactiveList } : {}),
                    ...(interactiveButtons ? { interactiveButtons } : {}),
                    ...(options?.mentions && options.mentions.length > 0 ? { mentions: options.mentions } : {}),
                    ...(options?.previewUrl !== undefined ? { previewUrl: options.previewUrl } : {}),
                    ...(options?.instagramReplyMode ? { instagramReplyMode: options.instagramReplyMode } : {}),
                    ...(options?.messengerReplyMode ? { messengerReplyMode: options.messengerReplyMode } : {}),
                }),
            });
            const item = pickSingle(payload);
            return item ? normalizeMessage(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    addAttachmentMessage: async (
        conversationId: string,
        sender: ConversationParticipantRef,
        file: File,
        content?: string,
        options?: AddConversationMessageOptions
    ): Promise<Message | null> => {
        try {
            const formData = new FormData();
            appendSenderToFormData(formData, sender);
            formData.append('file', file);

            const trimmedContent = content?.trim();
            if (trimmedContent) {
                formData.append('content', trimmedContent);
            }

            if (options?.quotedMessageId) {
                formData.append('quotedMessageId', options.quotedMessageId);
            }

            if (options?.previewUrl !== undefined) {
                formData.append('previewUrl', String(options.previewUrl));
            }

            const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/messages/attachment`, {
                method: 'POST',
                body: formData,
            });
            const item = pickSingle(payload);
            return item ? normalizeMessage(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    createConversation: async (
        title?: string,
        participantsInput?: ConversationParticipantRef[],
        options?: { channelAccountId?: string },
    ): Promise<Conversation> => {
        let participants = participantsInput
            ?.filter(p => p && typeof p.id === 'string' && p.id.trim().length > 0)
            .map(p => ({
                ...p,
                id: p.id.trim(),
                ...(typeof p.contactChannelId === 'string' && p.contactChannelId.trim()
                    ? { contactChannelId: p.contactChannelId.trim() }
                    : {}),
            })) || [];

        if (participants.length === 0) {
            throw new Error('Cannot create conversation: at least one participant is required.');
        }

        const channelAccountId = options?.channelAccountId?.trim();
        const payload = await apiRequest<any>('/api/v1/conversations', {
            method: 'POST',
            body: JSON.stringify({
                title: title || undefined,
                participants,
                ...(channelAccountId ? { channelAccountId } : {}),
            }),
        });
        const item = pickSingle(payload);
        if (!item) {
            throw new Error('Backend returned empty conversation payload.');
        }
        return normalizeConversation(item);
    },

    addParticipants: async (conversationId: string, participantsInput: ConversationParticipantRef[]): Promise<Conversation | null> => {
        const participants = participantsInput
            .filter(p => p && typeof p.id === 'string' && p.id.trim().length > 0)
            .map(p => ({ ...p, id: p.id.trim() }));

        if (participants.length === 0) {
            throw new Error('At least one participant is required.');
        }

        try {
            const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/participants`, {
                method: 'POST',
                body: JSON.stringify({ participants } satisfies AddConversationParticipantsInput),
            });
            const item = pickSingle(payload);
            return item ? normalizeConversation(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    signConversation: async (id: string): Promise<Conversation | null> => {
        try {
            const payload = await apiRequest<any>(`/api/v1/conversations/${id}/sign`, {
                method: 'POST',
                body: JSON.stringify({}),
            });
            const item = pickSingle(payload);
            return item ? normalizeConversation(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    toggleAiAssistant: async (id: string, muted: boolean): Promise<Conversation | null> => {
        try {
            const payload = await apiRequest<any>(`/api/v1/conversations/${id}/ai-assistant`, {
                method: 'PATCH',
                body: JSON.stringify({ muted }),
            });
            const item = pickSingle(payload);

            if (item) {
                return normalizeConversation(item);
            }

            // Some deployments return 200/204 without a response body for this endpoint.
            // Refresh the conversation so UI state updates immediately after toggle.
            return await conversationService.getConversationById(id);
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    getConversationAiSettings: async (id: string): Promise<ConversationAiSettings> => {
        const payload = await apiRequest<any>(`/api/v1/conversations/${id}/ai-assistant/settings`);
        return normalizeConversationAiSettings(pickSingle(payload));
    },

    updateConversationAiSettings: async (
        id: string,
        request: UpdateConversationAiSettingsRequest,
    ): Promise<ConversationAiSettings> => {
        const payload = await apiRequest<any>(`/api/v1/conversations/${id}/ai-assistant/settings`, {
            method: 'PUT',
            body: JSON.stringify({
                instructionOverride: request.instructionOverride?.trim() || null,
                inheritTools: request.inheritTools,
                enabledFunctionIds: request.enabledFunctionIds,
            }),
        });
        return normalizeConversationAiSettings(pickSingle(payload));
    },

    generateAiDraft: async (conversationId: string, instruction?: string): Promise<AiDraftSuggestion | null> => {
        try {
            const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/ai-assistant/draft`, {
                method: 'POST',
                body: JSON.stringify({ instruction: instruction?.trim() || undefined }),
            });
            const item = pickSingle(payload);
            return item ? normalizeAiDraftSuggestion(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    generateAiSummary: async (conversationId: string, instruction?: string): Promise<AiConversationSummary | null> => {
        try {
            const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/ai-assistant/summary`, {
                method: 'POST',
                body: JSON.stringify({ instruction: instruction?.trim() || undefined }),
            });
            const item = pickSingle(payload);
            return item ? normalizeAiConversationSummary(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    getAiMemory: async (conversationId: string): Promise<ConversationMemoryState | null> => {
        try {
            const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/ai-assistant/memory`, {
                method: 'GET',
            });
            const item = pickSingle(payload);
            return item ? normalizeConversationMemoryState(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    refreshAiMemory: async (conversationId: string, instruction?: string): Promise<ConversationMemoryState | null> => {
        try {
            const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/ai-assistant/memory/refresh`, {
                method: 'POST',
                body: JSON.stringify({ instruction: instruction?.trim() || undefined }),
            });
            const item = pickSingle(payload);
            return item ? normalizeConversationMemoryState(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    clearAiMemory: async (conversationId: string): Promise<ConversationMemoryState | null> => {
        try {
            const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/ai-assistant/memory`, {
                method: 'DELETE',
            });
            const item = pickSingle(payload);
            return item ? normalizeConversationMemoryState(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    softDeleteMessage: async (conversationId: string, messageId: string): Promise<void> => {
        await apiRequest<unknown>(`/api/v1/conversations/${conversationId}/messages/${messageId}`, {
            method: 'DELETE',
        });
    },

    softDeleteConversation: async (conversationId: string): Promise<void> => {
        await apiRequest<unknown>(`/api/v1/conversations/${encodeURIComponent(conversationId)}`, {
            method: 'DELETE',
        });
    },

    updatePreferences: async (id: string, input: UpdateConversationPreferencesInput): Promise<Conversation | null> => {
        try {
            const query = new URLSearchParams({ messagePageSize: String(DEFAULT_CONVERSATION_MESSAGE_PAGE_SIZE) });
            const payload = await apiRequest<any>(`/api/v1/conversations/${id}?${query.toString()}`, {
                method: 'PATCH',
                body: JSON.stringify({
                    ...(typeof input.isFavorite === 'boolean' ? { isFavorite: input.isFavorite } : {}),
                    ...(typeof input.isPinned === 'boolean' ? { isPinned: input.isPinned } : {}),
                    ...(typeof input.isSpam === 'boolean' ? { isSpam: input.isSpam } : {}),
                    ...(typeof input.isHidden === 'boolean' ? { isHidden: input.isHidden } : {}),
                    ...(input.note !== undefined ? { note: input.note } : {}),
                    ...(Object.prototype.hasOwnProperty.call(input, 'aiHistoryStartAtUtc') ? { aiHistoryStartAtUtc: input.aiHistoryStartAtUtc } : {}),
                }),
            });
            const item = pickSingle(payload);
            return item ? normalizeConversation(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    updateAssignment: async (id: string, input: UpdateConversationAssignmentInput): Promise<Conversation | null> => {
        try {
            const payload = await apiRequest<any>(`/api/v1/conversations/${id}/assignment`, {
                method: 'PATCH',
                body: JSON.stringify({ assignedToEmployeeId: input.assignedToEmployeeId }),
            });
            const item = pickSingle(payload);
            return item ? normalizeConversation(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    assignToMe: async (id: string): Promise<Conversation | null> => {
        try {
            const payload = await apiRequest<any>(`/api/v1/conversations/${id}/assignment/me`, {
                method: 'POST',
                body: JSON.stringify({}),
            });
            const item = pickSingle(payload);
            return item ? normalizeConversation(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    getNotes: async (conversationId: string): Promise<ConversationNotesState | null> => {
        try {
            const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/notes`);
            const item = pickSingle(payload);
            return item ? normalizeConversationNotesState(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    getAiOperations: async (conversationId: string, options: GetConversationAiOperationsOptions = {}): Promise<ConversationAiOperationsPage | null> => {
        try {
            const query = new URLSearchParams();

            if (typeof options.limit === 'number' && options.limit > 0) {
                query.set('limit', String(options.limit));
            }

            if (options.before?.trim()) {
                query.set('before', options.before.trim());
            }

            if (options.status?.trim()) {
                query.set('status', options.status.trim());
            }

            if (options.plugin?.trim()) {
                query.set('plugin', options.plugin.trim());
            }

            if (options.triggeredBy?.trim()) {
                query.set('triggeredBy', options.triggeredBy.trim());
            }

            if (options.runId?.trim()) {
                query.set('runId', options.runId.trim());
            }

            const suffix = query.size > 0 ? `?${query.toString()}` : '';
            const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/ai-operations${suffix}`);
            const item = pickSingle(payload);
            return item ? normalizeConversationAiOperationsPage(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    listTasks: async (conversationId: string): Promise<TaskItem[]> => {
        const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/tasks`);
        return pickItems(payload).map(normalizeTask).filter(task => task.id && task.title);
    },

    linkExistingTask: async (conversationId: string, taskId: string, input: LinkConversationTaskInput = {}): Promise<TaskItem | null> => {
        const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/tasks/${taskId}`, {
            method: 'POST',
            body: JSON.stringify({
                ...(input.messageId ? { messageId: input.messageId } : {}),
                ...(typeof input.isPrimary === 'boolean' ? { isPrimary: input.isPrimary } : {}),
            }),
        });
        const item = pickSingle(payload);
        return item ? normalizeTask(item) : null;
    },

    createTask: async (conversationId: string, request: CreateTaskRequest): Promise<TaskItem | null> => {
        const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/tasks`, {
            method: 'POST',
            body: JSON.stringify(toConversationTaskRequest(request)),
        });
        const item = pickSingle(payload);
        return item ? normalizeTask(item) : null;
    },

    createTaskFromMessage: async (conversationId: string, messageId: string, request: CreateTaskRequest): Promise<TaskItem | null> => {
        const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/messages/${messageId}/tasks`, {
            method: 'POST',
            body: JSON.stringify(toConversationTaskRequest(request)),
        });
        const item = pickSingle(payload);
        return item ? normalizeTask(item) : null;
    },

    createNote: async (conversationId: string, input: CreateConversationNoteInput): Promise<ConversationNotesState | null> => {
        try {
            const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/notes`, {
                method: 'POST',
                body: JSON.stringify(input),
            });
            const item = pickSingle(payload);
            return item ? normalizeConversationNotesState(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    updateNote: async (conversationId: string, noteId: string, input: UpdateConversationNoteInput): Promise<ConversationNotesState | null> => {
        try {
            const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/notes/${noteId}`, {
                method: 'PUT',
                body: JSON.stringify(input),
            });
            const item = pickSingle(payload);
            return item ? normalizeConversationNotesState(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    deleteNote: async (conversationId: string, noteId: string): Promise<ConversationNotesState | null> => {
        try {
            const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/notes/${noteId}`, {
                method: 'DELETE',
            });
            const item = pickSingle(payload);
            return item ? normalizeConversationNotesState(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    markAsRead: async (
        conversationId: string,
        messageId: string,
        reader: ConversationParticipantRef,
        options?: MarkMessagesReadOptions,
    ): Promise<boolean> => {
        try {
            await apiRequest<any>(`/api/v1/conversations/${conversationId}/messages/${messageId}/read`, {
                method: 'POST',
                body: JSON.stringify({
                    reader,
                    ...(options?.showWhatsAppTypingIndicator ? { showWhatsAppTypingIndicator: true } : {}),
                    ...(options?.showInstagramTypingIndicator ? { showInstagramTypingIndicator: true } : {}),
                    ...(options?.showMessengerTypingIndicator ? { showMessengerTypingIndicator: true } : {}),
                }),
            });
            return true;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return false;
            }
            throw error;
        }
    },

    markManyAsRead: async (
        conversationId: string,
        messageIds: string[],
        reader: ConversationParticipantRef,
        options?: MarkMessagesReadOptions,
    ): Promise<boolean> => {
        const normalizedMessageIds = Array.from(new Set(messageIds.map(id => id.trim()).filter(Boolean)));
        if (normalizedMessageIds.length === 0) {
            return true;
        }

        try {
            await apiRequest<any>(`/api/v1/conversations/${conversationId}/messages/read`, {
                method: 'POST',
                body: JSON.stringify({
                    reader,
                    messageIds: normalizedMessageIds,
                    ...(options?.showWhatsAppTypingIndicator ? { showWhatsAppTypingIndicator: true } : {}),
                    ...(options?.showInstagramTypingIndicator ? { showInstagramTypingIndicator: true } : {}),
                    ...(options?.showMessengerTypingIndicator ? { showMessengerTypingIndicator: true } : {}),
                }),
            });
            return true;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return false;
            }
            throw error;
        }
    },

    updateConversationTyping: async (
        conversationId: string,
        reader: ConversationParticipantRef,
        isTyping: boolean,
    ): Promise<void> => {
        await apiRequest<void>(`/api/v1/conversations/${conversationId}/typing`, {
            method: 'PUT',
            body: JSON.stringify({
                participant: reader,
                isTyping,
            }),
        });
    },

    toggleReaction: async (conversationId: string, messageId: string, emoji: string, user: ConversationParticipantRef): Promise<Conversation | null> => {
        try {
            const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/messages/${messageId}/reactions/toggle`, {
                method: 'POST',
                body: JSON.stringify({ emoji, user }),
            });
            const item = pickSingle(payload);
            return item ? normalizeConversation(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    moderateInstagramComment: async (
        conversationId: string,
        messageId: string,
        action: 'hide' | 'unhide' | 'delete',
    ): Promise<Message | null> => {
        try {
            const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/messages/${messageId}/instagram-comment/moderation`, {
                method: 'POST',
                body: JSON.stringify({ action }),
            });
            const item = pickSingle(payload);
            return item ? normalizeMessage(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    moderateMessengerComment: async (
        conversationId: string,
        messageId: string,
        action: 'hide' | 'unhide' | 'delete',
    ): Promise<Message | null> => {
        try {
            const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/messages/${messageId}/messenger-comment/moderation`, {
                method: 'POST',
                body: JSON.stringify({ action }),
            });
            const item = pickSingle(payload);
            return item ? normalizeMessage(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    transcribeMessage: async (conversationId: string, messageId: string): Promise<Message | null> => {
        try {
            const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/messages/${messageId}/transcribe`, {
                method: 'POST',
                body: JSON.stringify({}),
            });
            const item = pickSingle(payload);
            return item ? normalizeMessage(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    scheduleMessage: async (
        conversationId: string,
        sender: ConversationParticipantRef,
        content: string,
        scheduledAtUtc: Date,
        quotedMessageId?: string
    ): Promise<ScheduledMessage | null> => {
        try {
            const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/messages/schedule`, {
                method: 'POST',
                body: JSON.stringify({
                    sender,
                    content,
                    ...(quotedMessageId ? { quotedMessageId } : {}),
                    scheduledAtUtc: scheduledAtUtc.toISOString(),
                    messageType: 'text',
                }),
            });
            const item = pickSingle(payload);
            return item ? normalizeScheduledMessage(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    scheduleAttachmentMessage: async (
        conversationId: string,
        sender: ConversationParticipantRef,
        file: File,
        scheduledAtUtc: Date,
        content?: string,
        quotedMessageId?: string
    ): Promise<ScheduledMessage | null> => {
        try {
            const formData = new FormData();
            appendSenderToFormData(formData, sender);
            formData.append('file', file);
            formData.append('scheduledAtUtc', scheduledAtUtc.toISOString());

            const trimmedContent = content?.trim();
            if (trimmedContent) {
                formData.append('content', trimmedContent);
            }

            if (quotedMessageId) {
                formData.append('quotedMessageId', quotedMessageId);
            }

            const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/messages/schedule/attachment`, {
                method: 'POST',
                body: formData,
            });
            const item = pickSingle(payload);
            return item ? normalizeScheduledMessage(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    getScheduledMessages: async (conversationId: string): Promise<ScheduledMessage[]> => {
        const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/messages/scheduled`);
        return pickItems(payload)
            .map(normalizeScheduledMessage)
            .filter(item => item.id && item.conversationId);
    },

    listScheduledInboxMessages: async (): Promise<ScheduledInboxItem[]> => {
        const payload = await apiRequest<any>('/api/v1/conversations/messages/scheduled');
        return pickItems(payload)
            .map((value: any): ScheduledInboxItem => ({
                id: String(value?.id ?? ''),
                conversationId: String(value?.conversationId ?? ''),
                conversationTitle: String(value?.conversationTitle ?? '').trim() || 'Conversation',
                content: String(value?.content ?? ''),
                messageType: typeof value?.messageType === 'string' && value.messageType.trim()
                    ? value.messageType.trim().toLowerCase()
                    : 'text',
                fileName: typeof value?.fileName === 'string' && value.fileName.trim() ? value.fileName.trim() : undefined,
                scheduledAtUtc: normalizeUtcTimestamp(value?.scheduledAtUtc),
            }))
            .filter((item: ScheduledInboxItem) => item.id && item.conversationId);
    },

    cancelScheduledMessage: async (conversationId: string, scheduledMessageId: string): Promise<ScheduledMessage | null> => {
        try {
            const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/messages/scheduled/${scheduledMessageId}`, {
                method: 'DELETE',
            });
            const item = pickSingle(payload);
            return item ? normalizeScheduledMessage(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    updateScheduledMessageTime: async (conversationId: string, scheduledMessageId: string, scheduledAtUtc: Date, content?: string): Promise<ScheduledMessage | null> => {
        try {
            const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/messages/scheduled/${scheduledMessageId}`, {
                method: 'PATCH',
                body: JSON.stringify({
                    scheduledAtUtc: scheduledAtUtc.toISOString(),
                    hasContent: typeof content === 'string',
                    content: typeof content === 'string' ? content : undefined,
                }),
            });
            const item = pickSingle(payload);
            return item ? normalizeScheduledMessage(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    getInstagramChannelDefaults: async (): Promise<{ defaultCommentReplyMode: 'public_comment' | 'private_comment' }> => {
        const payload = await apiRequest<any>('/api/v1/instagram/settings/channel-defaults');
        const item = pickSingle(payload) ?? payload;
        return {
            defaultCommentReplyMode: item?.defaultCommentReplyMode === 'private_comment' ? 'private_comment' : 'public_comment',
        };
    },
};

export const getConversations = conversationService.getConversations;
export const getConversationsPage = conversationService.getConversationsPage;
export const listConversationCustomLists = conversationService.listCustomLists;
export const createConversationCustomList = conversationService.createCustomList;
export const updateConversationCustomList = conversationService.updateCustomList;
export const deleteConversationCustomList = conversationService.deleteCustomList;
export const moveConversationToCustomList = conversationService.moveConversationToCustomList;
export const moveConversationsToCustomList = conversationService.moveConversationsToCustomList;
export const getConversationById = conversationService.getConversationById;
export const getConversationMessagesPage = conversationService.getConversationMessagesPage;
export const getParticipantOptions = conversationService.getParticipantOptions;
export const getMentionSuggestions = conversationService.getMentionSuggestions;
export const findExistingDirectConversation = conversationService.findExistingDirectConversation;
export const findExistingExternalConversation = conversationService.findExistingExternalConversation;
export const addMessage = conversationService.addMessage;
export const addAttachmentMessage = conversationService.addAttachmentMessage;
export const createConversation = conversationService.createConversation;
export const addConversationParticipants = conversationService.addParticipants;
export const signConversation = conversationService.signConversation;
export const toggleAiAssistant = conversationService.toggleAiAssistant;
export const getConversationAiSettings = conversationService.getConversationAiSettings;
export const updateConversationAiSettings = conversationService.updateConversationAiSettings;
export const generateAiDraft = conversationService.generateAiDraft;
export const generateAiSummary = conversationService.generateAiSummary;
export const getAiMemory = conversationService.getAiMemory;
export const refreshAiMemory = conversationService.refreshAiMemory;
export const clearAiMemory = conversationService.clearAiMemory;
export const updateConversationPreferences = conversationService.updatePreferences;
export const softDeleteMessage = conversationService.softDeleteMessage;
export const softDeleteConversation = conversationService.softDeleteConversation;
export const updateConversationAssignment = conversationService.updateAssignment;
export const assignConversationToMe = conversationService.assignToMe;
export const getConversationNotes = conversationService.getNotes;
export const getConversationAiOperations = conversationService.getAiOperations;
export const listConversationTasks = conversationService.listTasks;
export const linkExistingTaskToConversation = conversationService.linkExistingTask;
export const createConversationTask = conversationService.createTask;
export const createConversationTaskFromMessage = conversationService.createTaskFromMessage;
export const createConversationNote = conversationService.createNote;
export const updateConversationNote = conversationService.updateNote;
export const deleteConversationNote = conversationService.deleteNote;
export const markAsRead = conversationService.markAsRead;
export const markManyAsRead = conversationService.markManyAsRead;
export const updateConversationTyping = conversationService.updateConversationTyping;
export const toggleReaction = conversationService.toggleReaction;
export const moderateInstagramComment = conversationService.moderateInstagramComment;
export const moderateMessengerComment = conversationService.moderateMessengerComment;
export const getInstagramChannelDefaults = conversationService.getInstagramChannelDefaults;
export const transcribeMessage = conversationService.transcribeMessage;
export const scheduleMessage = conversationService.scheduleMessage;
export const scheduleAttachmentMessage = conversationService.scheduleAttachmentMessage;
export const getScheduledMessages = conversationService.getScheduledMessages;
export const listScheduledInboxMessages = conversationService.listScheduledInboxMessages;
export const cancelScheduledMessage = conversationService.cancelScheduledMessage;
export const updateScheduledMessageTime = conversationService.updateScheduledMessageTime;
export type { ScheduledMessage } from '../types/Conversation';